import moment from "moment";
import { Utilities } from "@tylertech/shared-module";
import { ICalendarDate, PropertyMetadata } from "@tylertech/tyler-api-types";
import { IOrderedCalendarDate } from "./metadataCalendarMonth.component";
import { MetadataEventService, MetadataPropertyService } from "../../services";
import { AugmentedModelObject, RepositoryFormData } from "../../interfaces";
import { Component, ElementRef, Input, LOCALE_ID, SimpleChanges, ViewChild, OnInit, OnChanges, inject } from "@angular/core";
import { PopoverComponent } from "@tylertech/forge";
import { formatDate } from "@angular/common";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
    selector: "metadata-calendar-day",
    templateUrl: "./metadataCalendarDay.tmpl.html",
    standalone: false
})

export class MetadataCalendarDayComponent implements OnInit, OnChanges
{
    private $metadataPropertyService = inject(MetadataPropertyService);
    private $metadataEventService = inject(MetadataEventService);
    locale = inject(LOCALE_ID);

    @Input() formRepository: RepositoryFormData;
    @Input() propertyMetadata: PropertyMetadata;
    @Input() metadataModel: AugmentedModelObject;
    @Input() parent: AugmentedModelObject;

    @Input() date: Date;
    @Input() month: number;
    @Input() events: IOrderedCalendarDate[];
    @Input() displayedWeeks: Array<Date[]> = [];

    @ViewChild("popover") popover: PopoverComponent;
    @ViewChild("viewAllEventsDisplay") viewAllEventsDisplay: ElementRef<HTMLElement>;

    private firstDayOfRange = "calendar-range--start";
    private lastDayOfRange = "calendar-range--end";
    private eventLimit = 3;
    private refreshDates: boolean = false;
    private groupedEvents: Map<string, IOrderedCalendarDate[]>;
    private groupedEventsSet = false;

    constructor()
    {
        const $metadataEventService = this.$metadataEventService;

        const self = this;
        $metadataEventService.onRecalculateWorking.pipe(takeUntilDestroyed())
            .subscribe(args =>
            {
                if (!args.Recalculating && !args.ShowLoading)
                {
                    self._dayEvents = null;
                    self._allDayEvents = null;
                    self.setGroupedEvents();
                }
            });
    }

    ngOnInit(): void
    {
        this.setGroupedEvents();
    }

    ngOnChanges(changes: SimpleChanges)
    {
        if (changes["events"])
        {
            this._dayEvents = null;
            this._allDayEvents = null;
            this.setGroupedEvents();
        }
    }

    setGroupedEvents(): void
    {
        this.groupedEventsSet = false;
        const events = this.events
            .filter(x => x["$firstInRange"] === true)
            .sort((a, b) => (a.DateKey > b.DateKey) ? 1 : ((b.DateKey > a.DateKey) ? -1 : 0));

        if (!Utilities.IsNullOrUndefined(events))
        {
            const groupMap: Map<string, IOrderedCalendarDate[]> =
                Utilities.groupBy(
                    events,
                    x => x.DateKey);

            let displayIndex = 0;
            groupMap.forEach(group =>
            {
                if (group.length === 1)
                {
                    const day = new Date(group[0].Day);
                    const startDay = day.toDateString();

                    group[0].Key = `${group[0].DateKey}|-|${startDay}`;
                    group[0].DisplayIndex = displayIndex;
                    const groupEvents = this.events.filter(x => x.DateKey === group[0].DateKey && !x["$firstInRange"]);
                    groupEvents.forEach(event =>
                    {
                        event.Key = `${event.DateKey}|-|${startDay}`;
                        event.DisplayIndex = displayIndex;
                        group.push(event);
                    });
                }
                else
                {
                    let groupIndex = 0;
                    group.forEach(x =>
                    {
                        const currentDateKey = x.DateKey;
                        const day = new Date(x.Day);
                        const startDay = day.toDateString();

                        x.Key = `${currentDateKey}|-|${startDay}`;
                        x.DisplayIndex = displayIndex;
                        const groupEventsEnd = this.events.filter(event => event.DateKey === currentDateKey && event["$lastInRange"] === true);
                        const groupEvents = this.events.filter(
                            event => event.DateKey === currentDateKey && moment(event.Day).isBetween(moment(day), moment(groupEventsEnd[groupIndex].Day), undefined, "(]")
                        );
                        groupEvents.forEach(event =>
                        {
                            event.Key = `${event.DateKey}|-|${startDay}`;
                            event.DisplayIndex = displayIndex;
                            group.push(event);
                        });

                        groupIndex++;
                    });
                }

                displayIndex++;
            });

            this.groupedEvents = groupMap;
        }
        else
        {
            this.groupedEvents = null;
        }
        this.groupedEventsSet = true;
    }

    private _dayEvents: Map<string, IOrderedCalendarDate[]>;
    private _allDayEvents: Map<string, IOrderedCalendarDate[]>;
    dayEvents(day: Date): IOrderedCalendarDate[]
    {
        if (!this.groupedEventsSet)
        {
            return;
        }

        if (!Utilities.IsNullOrUndefined(this._dayEvents) && !Utilities.IsNullOrUndefined(this._dayEvents[day.toDateString()]))
        {
            return this._dayEvents[day.toDateString()];
        }

        let displayedEvents: IOrderedCalendarDate[] = [];

        const allEventsForDay = this.events
            .filter(x => new Date(day).toDateString() === new Date(x.Day).toDateString())
            .sort((a, b) => moment(new Date(a.Key.split("|-|")[1])).diff(moment(new Date(b.Key.split("|-|")[1]))) || a.DateKey.localeCompare(b.DateKey));

        if (Utilities.IsNullOrUndefined(this._allDayEvents))
        {
            this._allDayEvents = new Map<string, IOrderedCalendarDate[]>;
        }
        this._allDayEvents[day.toDateString()] = Utilities.deepCopy(allEventsForDay);

        allEventsForDay.forEach(event =>
        {
            if (!event["$firstInRange"])
            {
                displayedEvents.push(<IOrderedCalendarDate>{
                    DisplayIndex: event.DisplayIndex,
                    Key: event.Key
                });
            }
        });

        const endDate = moment(this.displayedWeeks[this.displayedWeeks.length - 1][6]).toDate();
        const dayOfWeek = new Date(day).getDay();

        const dayEvents = allEventsForDay.filter(x => x["$firstInRange"] === true);

        if (dayEvents.length > 0)
        {
            dayEvents.forEach(dayEvent =>
            {
                let addToDisplayedEvents = true;

                const groupedEvents = (this.groupedEvents.get(dayEvent.DateKey) as IOrderedCalendarDate[]);
                if (Utilities.IsNullOrUndefined(groupedEvents))
                {
                    return;
                }
                const groups: Map<string, IOrderedCalendarDate[]> = Utilities.groupBy(groupedEvents, x => x.Key);
                let groupedEvent: IOrderedCalendarDate[] = [];

                groups.forEach(group =>
                {
                    if (group.find(x => moment(x.Day).isSame(moment(day), "day")))
                    {
                        groupedEvent = group;
                        return;
                    }
                });

                if (!Utilities.IsNullOrUndefined(groupedEvent))
                {
                    const dayTotal = groupedEvent.length;

                    let dayTotalShown: number = groupedEvent.filter(x => new Date(x.Day) <= endDate).length;

                    if (dayOfWeek + dayTotalShown > 7)
                    {
                        dayTotalShown = 7 - dayOfWeek;
                    }
                    dayEvent.DayTotal = dayTotalShown;
                    if (dayEvent.DayTotal + dayOfWeek < 7 && dayTotalShown === dayTotal)
                    {
                        dayEvent["$lastInRange"] = true;
                    }

                    for (let i = this.eventLimit - 1; i >= 0; i--)
                    {
                        const event = displayedEvents[i];
                        if (!Utilities.IsNullOrUndefined(event))
                        {
                            if (event.DisplayIndex === dayEvent.DisplayIndex)
                            {
                                displayedEvents[i] = dayEvent;
                                addToDisplayedEvents = false;
                            }
                        }
                    }

                    if (addToDisplayedEvents)
                    {
                        displayedEvents.push(dayEvent);
                    }
                }
            });
        }

        if (dayOfWeek === 0)
        {
            const nonStartDateEvents = allEventsForDay.filter(x => !x["$firstInRange"]);

            if (nonStartDateEvents.length > 0)
            {
                nonStartDateEvents.forEach(nonStartDateEvent =>
                {
                    this.groupedEvents.get(nonStartDateEvent.DateKey).forEach(group =>
                    {

                        let addToDisplayedEvents = true;

                        if (day.toDateString() === group["$stringDate"])
                        {
                            const dayEventToAdd = Utilities.deepCopy(group) as IOrderedCalendarDate;
                            dayEventToAdd.DayTotal = this.events.filter(x => x.Key === group.Key && new Date(x.Day) >= new Date(day) && new Date(x.Day) <= endDate)?.length;

                            if (dayEventToAdd.DayTotal < 7)
                            {
                                if (new Date(day).getDate() !== endDate.getDate())
                                {
                                    dayEventToAdd["$lastInRange"] = true;
                                }
                            }
                            if (dayEventToAdd.DayTotal > 7)
                            {
                                dayEventToAdd.DayTotal = 7;
                            }

                            for (let i = this.eventLimit - 1; i >= 0; i--)
                            {
                                const event = displayedEvents[i];
                                if (!Utilities.IsNullOrUndefined(event))
                                {
                                    if (event.DisplayIndex === dayEventToAdd.DisplayIndex)
                                    {
                                        displayedEvents[i] = dayEventToAdd;
                                        addToDisplayedEvents = false;
                                    }
                                }
                            }

                            if (addToDisplayedEvents)
                            {
                                displayedEvents.push(dayEventToAdd);
                            }
                        }
                    });
                });
            }
        }

        displayedEvents = displayedEvents.slice(0, this.eventLimit);

        if (Utilities.IsNullOrUndefined(this._dayEvents))
        {
            this._dayEvents = new Map<string, IOrderedCalendarDate[]>;
        }
        this._dayEvents[day.toDateString()] = displayedEvents;

        return displayedEvents;
    }

    allDailyEvents(day: Date): IOrderedCalendarDate[]
    {
        if (Utilities.IsNullOrUndefined(this._allDayEvents) || Utilities.IsNullOrUndefined(this._allDayEvents[day.toDateString()]))
        {
            return null;
        }

        return this._allDayEvents[day.toDateString()];
    }

    eventMaxWidth(event: IOrderedCalendarDate): string
    {
        return `'calc(${event.DayTotal * 100}% - 16px)'`;
    }

    eventWidth(event: IOrderedCalendarDate): string
    {
        return `'calc(${event.DayTotal * 100}% + ${event.DayTotal - 2}px'`;
    }

    overflowEventsCount(day: Date): number
    {
        if (Utilities.IsNullOrUndefined(this._allDayEvents) || Utilities.IsNullOrUndefined(this._allDayEvents[day.toDateString()]))
        {
            return 0;
        }

        const displayed = this._dayEvents[day.toDateString()];
        const overlapEvents = displayed.filter(x => !x.DateKey);
        const allDayLength = this._allDayEvents[day.toDateString()].length;

        const yesterdayEvents = this._allDayEvents[moment(day).add(-1, "d")
            .toDate()
            .toDateString()];
        let counter = 0;
        overlapEvents.forEach(event =>
        {
            if (yesterdayEvents.findIndex(x => x.Key === event.Key) > 2)
            {
                counter++;
            }
        });

        if (counter > 0)
        {
            return allDayLength - counter;
        }
        else
        {
            return allDayLength - this.eventLimit;
        }
    }

    eventOverflow(day: Date): boolean
    {
        if (Utilities.IsNullOrUndefined(this._allDayEvents) || Utilities.IsNullOrUndefined(this._allDayEvents[day.toDateString()]))
        {
            return false;
        }

        const displayed = this._dayEvents[day.toDateString()];
        const overlapEvents = displayed.filter(x => !x.DateKey);
        const allDayLength = this._allDayEvents[day.toDateString()].length;

        const yesterdayEvents = this._allDayEvents[moment(day).add(-1, "d")
            .toDate()
            .toDateString()];
        let counter = 0;
        overlapEvents.forEach(event =>
        {
            if (yesterdayEvents.findIndex(x => x.Key === event.Key) > 2)
            {
                counter++;
            }
        });

        return counter > 0 || allDayLength > this.eventLimit;
    }

    dateInMonth(day: Date): boolean
    {
        return day.getMonth() === this.month;
    }

    showLabel(calendarDate: ICalendarDate): boolean
    {
        return calendarDate["$firstInRange"] || moment(this.date).day() === 0 || calendarDate["$showLabel"];
    }

    dateRangeClass(calendarDate: ICalendarDate): string
    {
        let ret = "";
        if (calendarDate["$firstInRange"] && !calendarDate["$previousDay"])
            ret += this.firstDayOfRange;
        if (calendarDate["$lastInRange"])
            ret += ` ${this.lastDayOfRange}`;
        return ret;
    }

    onEventKeyDown(event: KeyboardEvent, day: ICalendarDate): void
    {
        if (event.key === "Enter" || event.key === " ")
        {
            this.onClick(event, day);
        }
    }

    onClick($event: Event, dayEvent: ICalendarDate): void
    {
        $event.stopPropagation();

        const self = this;
        const clickedEvent = this.metadataModel[this.propertyMetadata.Name].find(x => x.Id === dayEvent.Id);
        if (clickedEvent)
            clickedEvent.Clicked = true;

        let type = this.metadataModel.$$TypeName;
        if (!type && this.formRepository) type = this.formRepository.Type;
        this.$metadataPropertyService.recalculateProperties(
            type,
            this.propertyMetadata,
            this.metadataModel,
            this.parent,
            this.metadataModel[this.propertyMetadata.Name],
            undefined,
            true,
            true,
            this.formRepository
        ).then(_x => self.metadataModel[self.propertyMetadata.Name].forEach(e => e.Clicked = false));

        this.popover.open = false;
    }

    hasDayEvents(day: Date): boolean
    {
        return !Utilities.IsNullOrUndefinedOrEmpty(this.dayEvents(day));
    }

    viewAllId(day: Date): string
    {
        return `viewAll-${this.month}-${formatDate(day, "MMddyyyy", this.locale)}`;
    }
}
